import { useRouter } from 'next/router';
import { castArray } from 'lodash';
import { ProfileUser } from 'lib/types/users';
import { PostSearchCriteria } from 'lib/types/filters';
import { queryToFilters } from 'lib/utils/filterUtils';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { ProfileMultiFilter } from 'components/MultiFilters/ProfileMultiFilter';
import { NetworkFilter } from 'components/Filters/NetworkFilter';
import { FileTypeFilter } from 'components/Filters/FileTypeFilter';
import { MoreFilter } from 'components/Filters/MoreFilter';
import { UserListFilter } from 'components/Filters/UserListFilter';
import { TimeframeOptionsFilter } from 'components/Filters/TimeframeOptionsFilter';
import { defaultTimeFrameOptions } from 'components/Filters/utils';
import { userIsAdmin, userIsPaidTeamMember } from 'lib/helpers';
import { useTeams } from 'lib/contexts/teamsContext';
import { CreditedStatusFilter } from 'components/Filters/SelectCreditedStatusFilter';

interface Props {
  profileUser: ProfileUser;
  criteria: PostSearchCriteria;
}

export const FilterCriteria = ({
  profileUser, criteria,
}: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const teams = useTeams();

  const filters = queryToFilters(router.query);

  // if (!criteria) return null;

  const showSuggestedCreditsFilter = currentUser?.id === profileUser.id
    || (currentUser?.id
      && currentUser?.managedAccounts?.length
      && currentUser?.managedAccounts.some(
        (account) => account.accountId === profileUser.id,
      )) || false;

  const multiFilterLabel = `Search @collaborators, @accounts, #tags, events, keywords, @${profileUser.username}'s roles`;

  return (
    <div className="flex flex-wrap">
      <div className="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <div className="col-span-2 md:col-span-1 flex">
          <div className="w-full">
            <UserListFilter />
          </div>
        </div>
        <div className="col-span-2 flex">
          <div className="w-full">
            <ProfileMultiFilter
              filters={filters}
              profileUser={profileUser}
              label={multiFilterLabel}
              useNaturalLanguage
            />
          </div>
        </div>
        <div className="col-span-1">
          <NetworkFilter
            networks={criteria.networks as string[]}
            includeSnapchat={userIsPaidTeamMember(currentUser, teams)}
          />
        </div>
        <div className="col-span-1">
          <FileTypeFilter
            fileTypes={criteria.fileTypes as string[]}
            includeStories={userIsPaidTeamMember(currentUser, teams)}
          />
        </div>
        <div className="col-span-1">
          <TimeframeOptionsFilter
            minPostedAt={criteria.minPostedAt}
            maxPostedAt={criteria.maxPostedAt}
            options={defaultTimeFrameOptions}
          />
        </div>
        <div className="col-span-1">
          <MoreFilter
            engagement={{
              ...(criteria.minViews && { minViews: criteria.minViews }),
              ...(criteria.maxViews && { maxViews: criteria.maxViews }),
              ...(criteria.minLikes && { minLikes: criteria.minLikes }),
              ...(criteria.maxLikes && { maxLikes: criteria.maxLikes }),
              ...(criteria.minShares && { minShares: criteria.minShares }),
              ...(criteria.maxShares && { maxShares: criteria.maxShares }),
              ...(criteria.minComments && { minComments: criteria.minComments }),
              ...(criteria.maxComments && { maxComments: criteria.maxComments }),
            }}
            includeUncredited={criteria.includeUncredited}
            roleTagSlugs={router.query.roleTags ? castArray(router.query.roleTags) : []}
          />
        </div>
        <div className="col-span-1">
          <CreditedStatusFilter
            creditedStatus={criteria.creditedStatus}
            defaultValue={currentUser?.id === profileUser.id ? 'all' : 'credited'}
            isProfileOwner={currentUser?.id === profileUser.id || userIsAdmin(currentUser)}
            showSuggestedCreditsFilter={!!showSuggestedCreditsFilter}
          />
        </div>
      </div>
    </div>
  );
};
